<template>
  <div
    class="content-block-wrapper flex flex-wrap"
    :class="{ 'flex-col': isComponentsOverviewPage }"
  >
    <template
      v-for="(component, index) in components"
      :key="`template-${index}`"
    >
      <ComponentName
        v-if="isComponentsOverviewPage"
        :key="`header-${index}`"
        :name="component.componentName"
      />
      <component
        :is="resolveCmsComponent(component.componentName)"
        v-bind="component.props"
        class="content-block"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from 'vue';
import type { AsyncComponentLoader, Component, PropType } from 'vue';
import { extractComponents } from '~~/helpers/cms/extractComponents';
import type { ComponentData, ExtractedComponent } from '~~/types/cms/CmsComponentData';
import ComponentName from '~/cms/ComponentName/ComponentName.vue';

/**
 * Auto-discover loaders for all .vue files under /cms (recursive).
 * We include both absolute-style and alias-style globs to be robust in different setups.
 */
const globPaths = {
  absolute: import.meta.glob('/cms/**/*.vue'),
  alias: import.meta.glob('~/cms/**/*.vue'),
};

// Merge the two glob maps into one map of path -> loader
const modules: Record<string, () => Promise<unknown>> = {
  ...globPaths.absolute,
  ...globPaths.alias,
};

// Normalized lookup map: component filename (without extension) -> loader
const loaderMap: Record<string, AsyncComponentLoader<Component>> = {};
for (const rawPath in modules) {

  // Normalize path key: remove leading ~/ or leading slashes, strip .vue
  const normalized = rawPath.replace(/^~\//, '').replace(/^\/+/, '').replace(/\.vue$/, ''); // e.g. "cms/widgets/MyWidget"
  const parts = normalized.split('/');
  const filename = parts[parts.length - 1];
  const parentFolder = parts.length >= 2 ? parts[parts.length - 2] : '';

  // If the file is an index.vue, map it to its parent folder name (e.g. cms/NotificationBannerCms/index.vue -> NotificationBannerCms)
  const key = filename === 'index' && parentFolder ? parentFolder : filename;

  if (key && !loaderMap[key]) {

    const loader = modules[rawPath];
    loaderMap[key] = async () => {

      const currentModule = await loader();

      if (currentModule && typeof currentModule === 'object' && 'default' in currentModule) {

        return (currentModule as { default: Component }).default;
      
      }

      return currentModule as Component;
    
    };
  
  }

}

const asyncCache: Record<string, ReturnType<typeof defineAsyncComponent>> = {};

export default defineComponent({
  name: 'RenderContent',
  components: {
    ComponentName,
  },
  props: {
    content: {
      type: [Array, Object] as PropType<ComponentData[] | ComponentData>,
      default: () => {

        return [] as ComponentData[];
      
      },
    },
    isComponentsOverviewPage: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    components(): ExtractedComponent[] {

      if (Array.isArray(this.content)) {

        return extractComponents(this.content);
      
      }

      if (this.content && typeof this.content === 'object') {

        return extractComponents([this.content]);
      
      }

      return [];
    
    },
  },
  methods: {
    resolveCmsComponent(componentName: string) {

      if (!componentName || typeof componentName !== 'string') {

        return null;

      }

      const key = componentName.replace(/\.vue$/, '');
      const loader = loaderMap[key];

      if (loader) {

        if (!asyncCache[key]) {

          asyncCache[key] = defineAsyncComponent(loader);
        
        }

        return asyncCache[key];
      
      }

      return null;
    
    },
  },
});
</script>

<style lang="scss" scoped>
.content-block {
  width: 100%;
}
</style>
